import { ENV_CONFIG } from 'config/environment';
import { useAuth0, AuthenticationError } from '@auth0/auth0-react';
import { clearLocalStorage, setAccessToken } from 'services/storage';
import { datadogLogs } from '@datadog/browser-logs';
import { env } from 'config/environment/env_schema/env';
import { mixpanelClient } from 'helpers/mixpanel';

export class TokenFetchError extends Error {
  constructor(public readonly cause: AuthenticationError) {
    super(`Token fetch failed: ${cause.error ?? cause.message}`);
    this.name = 'TokenFetchError';
    Object.setPrototypeOf(this, TokenFetchError.prototype);
  }
}

export const useAuth0Config = () => {
  const { getAccessTokenSilently, logout, loginWithRedirect } = useAuth0();

  const handleAccessToken = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: env.REACT_APP_AUTH0_AUDIENCE_V2,
          scope: env.REACT_APP_AUTH0_SCOPE_V2,
          redirect_uri: `${ENV_CONFIG().SITE_URL}/dashboard`,
        },
      });
      setAccessToken(token);
      return token;
    } catch (e) {
      const authError = e as AuthenticationError;

      datadogLogs.logger.debug(
        '[useAuth0Config] Failed to get token:',
        { errorCode: authError.error },
        authError
      );
      // invalid_grant usually means an expired token
      if (
        authError.error === 'invalid_grant' ||
        authError.error === 'Unknown or invalid refresh token'
      ) {
        // Re-authenticate user
        datadogLogs.logger.debug('Refresh token is invalid or expired. Redirecting to login.');
        loginWithRedirect({
          authorizationParams: {
            audience: env.REACT_APP_AUTH0_AUDIENCE_V2,
            scope: env.REACT_APP_AUTH0_SCOPE_V2,
            redirect_uri: `${ENV_CONFIG().SITE_URL}/dashboard`,
          },
          appState: {
            returnTo: window.location.pathname, // Optional: return user to current page after login
          },
        }).then(() => {
          datadogLogs.logger.debug('User logged in via redirect after token refresh failure.');
        });
        // Let the redirect handle recovery; do not rethrow here.
        return;
      }

      datadogLogs.logger.error('An unexpected authentication error occurred:', authError);
      throw new TokenFetchError(authError);
    }
  };

  const handleLogout = () => {
    mixpanelClient.reset();
    clearLocalStorage();
    logout({ logoutParams: { returnTo: ENV_CONFIG().SITE_URL } }).then(() =>
      datadogLogs.logger.debug('User logged out.')
    );
  };
  return { handleAccessToken, handleLogout };
};
